// Example: "ab***@gmail.com"
export function maskEmail(email: any): string {
  if (!email || typeof email !== "string") {
    return "[REDACTED]";
  }

  const trimmed = email.trim();
  const parts = trimmed.split("@");

  if (parts.length !== 2) {
    return "[REDACTED]";
  }

  const local = parts[0];
  const domain = parts[1];

  if (local.length <= 2) {
    return local[0] + "***@" + domain;
  }

  return local.slice(0, 2) + "***@" + domain;
}

// Example: "+91******3210"
export function maskPhone(phone: any): string {
  if (phone === null || phone === undefined) {
    return "[REDACTED]";
  }

  const str = String(phone).trim();

  if (str.length <= 6) {
    return "***" + str.slice(-2);
  }

  const prefix = str.slice(0, 2);
  const suffix = str.slice(-4);
  const middle = "*".repeat(str.length - 6);

  return prefix + middle + suffix;
}

// Example: "Ab*******"
export function maskName(name: any): string {
  if (!name || typeof name !== "string") {
    return "[REDACTED]";
  }

  const trimmed = name.trim();

  if (trimmed.length <= 2) {
    return trimmed[0] + "*";
  }

  return trimmed.slice(0, 2) + "*".repeat(trimmed.length - 2);
}

function isEmailField(key: string): boolean {
  return key.toLowerCase().includes("email");
}

function isPhoneField(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return (
    lowerKey.includes("phone") ||
    lowerKey === "mobile" ||
    lowerKey.includes("contactno") ||
    lowerKey.includes("whatsapp")
  );
}

function isNameField(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return (
    lowerKey === "name" ||
    lowerKey === "username" ||
    lowerKey === "fullname" ||
    lowerKey === "firstname" ||
    lowerKey === "lastname" ||
    lowerKey === "fname" ||
    lowerKey === "lname"
  );
}

function maskFieldByKey(key: string, value: any): string | null {
  if (isEmailField(key)) {
    return maskEmail(value);
  }
  if (isPhoneField(key)) {
    return maskPhone(value);
  }
  if (isNameField(key)) {
    return maskName(value);
  }
  return null;
}

const PII_KEYS = [
  "name",
  "username",
  "fullname",
  "firstname",
  "lastname",
  "fname",
  "lname",
  "whatsapp",
  "contactno",
  "mobile",
  "phone",
];

export function deepScrub(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    const result = [];
    for (const item of obj) {
      result.push(deepScrub(item));
    }
    return result;
  }

  if (typeof obj === "object") {
    const proto = Object.getPrototypeOf(obj);
    const isPlainObject = proto === null || proto === Object.prototype;

    // Not a plain object (e.g. a Date, a Mongo ObjectID, a class instance).
    // Leave it alone, except for Mongo ObjectIDs which we convert to text.
    if (!isPlainObject) {
      const ctorName = obj.constructor ? obj.constructor.name : "";
      if (
        ctorName === "ObjectID" ||
        ctorName === "ObjectId" ||
        obj._bsontype === "ObjectID"
      ) {
        return obj.toString();
      }
      return obj;
    }

    const scrubbed: any = {};

    for (const key of Object.keys(obj)) {
      const value = obj[key];

      if (value === null || value === undefined) {
        scrubbed[key] = value;
        continue;
      }

      if (typeof value === "string" || typeof value === "number") {
        const maskedValue = maskFieldByKey(key, value);
        scrubbed[key] = maskedValue !== null ? maskedValue : value;
      } else if (typeof value === "object") {
        scrubbed[key] = deepScrub(value);
      } else {
        scrubbed[key] = value;
      }
    }

    return scrubbed;
  }

  return obj;
}

function looksLikeEmail(text: string): boolean {
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
  return emailRegex.test(text);
}

function looksLikePhone(text: string): boolean {
  const phoneRegex =
    /(?:\+?\d{1,3}[- ]?)?\(?\d{3}\)?[- ]?\d{3,4}[- ]?\d{4}\b|\b\d{10}\b/;
  const match = text.match(phoneRegex);

  if (!match) {
    return false;
  }

  // Don't treat a 13-digit millisecond timestamp (like 1690000000000) as a phone number.
  const isTimestamp = /^1\d{12}$/.test(match[0]);
  return !isTimestamp;
}

export function containsPII(value: any): boolean {
  if (value === null || value === undefined) {
    return false;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      if (containsPII(item)) {
        return true;
      }
    }
    return false;
  }

  if (typeof value === "object") {
    const proto = Object.getPrototypeOf(value);
    const isPlainObject = proto === null || proto === Object.prototype;
    if (!isPlainObject) {
      return false;
    }

    for (const key of Object.keys(value)) {
      const fieldValue = value[key];
      const isPiiKey =
        isEmailField(key) || isPhoneField(key) || isNameField(key);

      if (isPiiKey && fieldValue !== null && fieldValue !== undefined) {
        return true;
      }

      if (containsPII(fieldValue)) {
        return true;
      }
    }

    return false;
  }

  if (typeof value === "string") {
    // If the string is actually JSON, check inside it.
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === "object") {
        return containsPII(parsed);
      }
    } catch (_e) {
      // not JSON, that's fine, keep checking as plain text
    }

    if (looksLikeEmail(value)) {
      return true;
    }

    if (looksLikePhone(value)) {
      return true;
    }

    for (const key of PII_KEYS) {
      const keyValRegex = new RegExp("\\b" + key + "\\b\\s*[:=]\\s*", "i");
      if (keyValRegex.test(value)) {
        return true;
      }
    }
  }

  return false;
}

export function scrubString(str: string): string {
  // If the whole string is JSON, scrub it as an object instead.
  try {
    const parsed = JSON.parse(str);
    if (parsed && typeof parsed === "object") {
      return JSON.stringify(deepScrub(parsed));
    }
  } catch (_e) {
    // not JSON, continue with plain text scrubbing below
  }

  let scrubbed = str;

  // Mask emails
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
  scrubbed = scrubbed.replace(emailRegex, (match) => maskEmail(match));

  // Mask phone numbers
  const phoneRegex =
    /(?:\+?\d{1,3}[- ]?)?\(?\d{3}\)?[- ]?\d{3,4}[- ]?\d{4}\b|\b\d{10}\b/g;
  scrubbed = scrubbed.replace(phoneRegex, (match) => {
    const isTimestamp = /^1\d{12}$/.test(match);
    if (isTimestamp) {
      return match;
    }
    return maskPhone(match);
  });

  // Mask "key: value" or "key=value" patterns, e.g. `name: "John Smith"`
  for (const key of PII_KEYS) {
    const keyValRegex = new RegExp(
      "\\b(" +
        key +
        ")\\b(\\s*[:=]\\s*)(?:([\"'])(.*?)\\3|([^\\s,{}[\\]\r\n]+(?:\\s+[^\\s,{}[\\]\r\n]+)*))",
      "gi"
    );

    scrubbed = scrubbed.replace(
      keyValRegex,
      (fullMatch, keyName, separator, quoteChar, valQuoted, valUnquoted) => {
        const quote = quoteChar || "";
        const val = (
          valQuoted !== undefined ? valQuoted : valUnquoted || ""
        ).trim();

        let maskedVal;
        if (isEmailField(key)) {
          maskedVal = maskEmail(val);
        } else if (isPhoneField(key)) {
          maskedVal = maskPhone(val);
        } else {
          maskedVal = maskName(val);
        }

        return keyName + separator + quote + maskedVal + quote;
      }
    );
  }

  return scrubbed;
}

function scrubArg(arg: any): any {
  if (typeof arg === "string") {
    return scrubString(arg);
  }
  if (arg && typeof arg === "object") {
    return deepScrub(arg);
  }
  return arg;
}

export function setupConsolePIIFilter() {
  if (typeof window !== "undefined" && (window as any).__piiFilterInstalled) {
    return;
  }

  const originalLog = console.log;
  const originalInfo = console.info;
  const originalDebug = console.debug;
  const originalWarn = console.warn;
  const originalError = console.error;

  // Prevents infinite loops in case scrubbing itself ever calls console.log
  let isFiltering = false;

  console.log = function (...args: any[]) {
    if (isFiltering) {
      originalLog.apply(console, args);
      return;
    }
    isFiltering = true;
    try {
      const scrubbedArgs = args.map(scrubArg);
      originalLog.apply(console, scrubbedArgs);
    } finally {
      isFiltering = false;
    }
  };

  console.info = function (...args: any[]) {
    if (isFiltering) {
      originalInfo.apply(console, args);
      return;
    }
    isFiltering = true;
    try {
      const scrubbedArgs = args.map(scrubArg);
      originalInfo.apply(console, scrubbedArgs);
    } finally {
      isFiltering = false;
    }
  };

  console.debug = function (...args: any[]) {
    if (isFiltering) {
      originalDebug.apply(console, args);
      return;
    }
    isFiltering = true;
    try {
      const scrubbedArgs = args.map(scrubArg);
      originalDebug.apply(console, scrubbedArgs);
    } finally {
      isFiltering = false;
    }
  };

  console.warn = function (...args: any[]) {
    if (isFiltering) {
      originalWarn.apply(console, args);
      return;
    }
    isFiltering = true;
    try {
      const scrubbedArgs = args.map(scrubArg);
      originalWarn.apply(console, scrubbedArgs);
    } finally {
      isFiltering = false;
    }
  };

  console.error = function (...args: any[]) {
    if (isFiltering) {
      originalError.apply(console, args);
      return;
    }
    isFiltering = true;
    try {
      const scrubbedArgs = args.map(scrubArg);
      originalError.apply(console, scrubbedArgs);
    } finally {
      isFiltering = false;
    }
  };

  if (typeof window !== "undefined") {
    (window as any).__piiFilterInstalled = true;
  }
}
